(() => {
  const seriesOrder = ['element', 'zinc', 'prism', 'rollo', 'delta-v', 'forza', 'strata'];
  const stampOrder = ['ACL PRO', 'ACL COMP', 'ACO', 'NONE'];
  const variantOrder = ['R', 'L', 'H', 'S'];
  const seriesPattern = /\b(delta[ -]?v|element|zinc|prism|rollo|forza|helix|strata)\b/i;
  const stampPattern = /\b(ACL\s*(?:PRO|COMP)|ACO|unstamped|no stamp)\b/i;
  const exactStampPattern = /^(?:ACL(?:\s*(?:PRO|COMP))?|ACO|none|unstamped|no[ -]*stamp)$/i;
  const variantPattern = /(?:^|[\s/\-])([RLHS])(?=[\s(\-/]|$)/i;
  const normalizeSeries = value => value.toLowerCase().replace(/delta[ -]?v/, 'delta-v');
  const normalizeStamp = (value, series) => {
    const stamp = value.trim().toUpperCase();
    if (/^(?:NONE|UNSTAMPED|NO[ -]*STAMP)$/.test(stamp)) return 'NONE';
    // Certification is stored as bare ACL in the current catalog. Resolve its
    // badge from the real bag series; never invent an ACL variant for Strata.
    if (stamp === 'ACL') return ['rollo', 'delta-v', 'forza'].includes(series) ? 'ACL COMP' : ['element', 'zinc', 'prism'].includes(series) ? 'ACL PRO' : '';
    return stamp.replace(/ACL\s*/, 'ACL ');
  };
  function dimensions(options, variants) {
    return new Set(options.flatMap((name, index) => {
      const values = [...new Set(variants.map(variant => variant.options?.[index]))];
      const named = /series|weight|template|stamp|approval|certification|bag[ -]?type|\bvariant\b/i.test(name);
      const recognized = values.length && values.every(value => typeof value === 'string' && (seriesPattern.test(value) || stampPattern.test(value) || exactStampPattern.test(value.trim()) || /^(?:none|[ -]?[RLHS](?:\s*\([^)]*\))?|regular|standard|light|heavy)$/i.test(value.trim())));
      return named || recognized ? [index] : [];
    }));
  }
  function parse(variant, tags = [], managed) {
    const values = variant.options || [variant.title || ''];
    const text = values.filter((_, index) => !managed || managed.has(index)).join(' / ');
    const foundSeries = text.match(seriesPattern);
    const foundStamp = text.match(stampPattern);
    const taggedSeries = tags.filter(tag => /^(delta[ -]?v|element|zinc|prism|rollo|forza|helix|strata)$/i.test(tag));
    const taggedStamp = tags.filter(tag => exactStampPattern.test(tag.trim()));
    const series = foundSeries ? normalizeSeries(foundSeries[1]) : taggedSeries.length === 1 ? normalizeSeries(taggedSeries[0]) : '';
    const explicitUnstamped = /\b(?:un[ -]*stamped|no[ -]*stamp)\b/i.test(text) || text.split(/\s*\/\s*/).some(value => /^none$/i.test(value));
    const metadataStamp = variant.stamp ?? variant.certification ?? variant.metadata?.stamp ?? variant.metadata?.certification;
    const exactStamps = values.filter((value, index) => (!managed || managed.has(index)) && exactStampPattern.test(value.trim()));
    const explicitStamp = typeof metadataStamp === 'string' && exactStampPattern.test(metadataStamp.trim()) ? metadataStamp.trim() : exactStamps.length === 1 ? exactStamps[0].trim() : '';
    const stamp = normalizeStamp(explicitStamp || (explicitUnstamped ? 'NONE' : foundStamp ? foundStamp[1] : taggedStamp.length === 1 ? taggedStamp[0] : ''), series);
    const letter = text.match(variantPattern)?.[1]?.toUpperCase();
    const word = text.match(/\b(regular|standard|light|heavy)\b/i)?.[1]?.toLowerCase();
    const weight = letter || ({ regular: 'R', standard: 'R', light: 'L', heavy: 'H' }[word]) || (['ACL COMP', 'ACO'].includes(stamp) ? 'R' : '');
    const allowed = { 'ACL PRO': ['R', 'L', 'H'], 'ACL COMP': ['R'], ACO: ['R', 'S'], NONE: ['R', 'L', 'H', 'S'] };
    const supported = seriesOrder.includes(series) && !!allowed[stamp]?.includes(weight) && !(series === 'strata' && stamp.startsWith('ACL')) && !(stamp === 'ACO' && ['rollo', 'delta-v'].includes(series));
    return { ...variant, series, stamp, weight, supported, available: variant.available ?? variant.availableForSale ?? false };
  }
  function sameUnrelated(variant, current, managed) {
    return variant.options.length === current.options.length && variant.options.every((value, index) => managed.has(index) || value === current.options[index]);
  }
  function candidates(variants, current, managed, filter = {}) {
    return variants.filter(variant => variant.supported && sameUnrelated(variant, current, managed) && Object.entries(filter).every(([key, value]) => variant[key] === value));
  }
  function target(variants, current, managed, filter, preferredWeight = current.weight) {
    return candidates(variants, current, managed, filter).filter(variant => variant.available).sort((a, b) => {
      const score = variant => (variant.series === current.series ? 100 : 0) + (variant.weight === preferredWeight ? 20 : variant.weight === 'R' ? 10 : 0);
      return score(b) - score(a);
    })[0] || null;
  }
  function nativeSelections(picker, variant) {
    if (!picker || !Array.isArray(variant.options)) return null;
    const groups = [...picker.querySelectorAll('.product-form__input')];
    const selections = variant.options.map((value, index) => {
      const group = groups[index];
      const select = group?.querySelector('select');
      const control = select ? [...select.options].find(option => option.value === value) : [...(group?.querySelectorAll('input[type="radio"]') || [])].find(input => input.value === value);
      return control?.dataset.optionValueId && !control.dataset.productUrl ? { group, select, control, value } : control?.dataset.optionValueId && control.dataset.productUrl === picker.closest('product-info')?.dataset.url ? { group, select, control, value } : null;
    });
    return groups.length === variant.options.length && selections.every(Boolean) ? selections : null;
  }
  function applySelections(selections) {
    selections.forEach(({ group, select, control, value }) => {
      if (select) {
        select.value = value;
        [...select.options].forEach(option => option === control ? option.setAttribute('selected', 'selected') : option.removeAttribute('selected'));
      } else group.querySelectorAll('input[type="radio"]').forEach(input => { input.checked = input === control; });
    });
  }
  const model = { seriesOrder, stampOrder, variantOrder, dimensions, parse, candidates, target, nativeSelections, applySelections };
  globalThis.LogicStampModel = model;
  class LogicStampPicker extends (globalThis.HTMLElement || class {}) {
    connectedCallback() {
      if (this.abort) return;
      this.productInfo = this.closest('product-info');
      this.native = this.querySelector('[data-stamp-native]');
      this.ui = this.querySelector('[data-stamp-ui]');
      this.status = this.querySelector('[data-stamp-status]');
      if (!this.productInfo || !this.native || !this.ui) return;
      try { this.data = JSON.parse(this.querySelector('[data-stamp-data]').textContent); } catch { return; }
      if (!Array.isArray(this.data.options) || !Array.isArray(this.data.variants) || this.data.variantCount !== this.data.variants.length || !this.data.variants.length || this.data.variants.some(variant => !Array.isArray(variant.options) || variant.options.length !== this.data.options.length)) return;
      this.managed = dimensions(this.data.options, this.data.variants);
      if (!this.managed.size) return;
      this.variants = this.data.variants.map(variant => parse(variant, this.data.tags, this.managed));
      const signatures = new Map();
      this.variants.forEach(variant => {
        const signature = JSON.stringify([variant.stamp, variant.series, variant.weight, variant.options.filter((_, index) => !this.managed.has(index))]);
        if (signatures.has(signature)) { variant.supported = false; signatures.get(signature).supported = false; }
        else signatures.set(signature, variant);
      });
      this.preferences = {};
      this.abort = new AbortController();
      const eventOptions = { signal: this.abort.signal };
      this.addEventListener('click', event => {
        const button = event.target.closest('button[data-stamp-choice]');
        if (button && this.contains(button)) this.choose(button);
        if (event.target.closest('[data-stamp-show-native]')) this.showNative();
      }, eventOptions);
      this.native.addEventListener('change', () => {
        if (!this.internalChange) { this.nativeSelectionChanged = true; this.preferenceIntent = null; }
        this.setBusy(true);
      }, eventOptions);
      this.productInfo.addEventListener('product-info:error', () => this.fail(), eventOptions);
      window.addEventListener('popstate', () => {
        const id = new URL(window.location.href).searchParams.get('variant');
        const variant = this.variants.find(item => String(item.id) === id);
        if (variant && String(variant.id) !== String(this.current?.id)) this.selectVariant(variant, { history: true });
      }, eventOptions);
      this.observer = new MutationObserver(() => {
        if (this.native.querySelector('variant-selects') !== this.picker) this.sync();
      });
      this.observer.observe(this.native, { childList: true, subtree: true });
      if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') this.unsubscribe = subscribe(PUB_SUB_EVENTS.variantChange, ({ data }) => {
        if (String(data.sectionId) !== String(this.productInfo.dataset.section)) return;
        this.sync(data.variant);
      });
      this.sync();
    }
    disconnectedCallback() {
      this.abort?.abort();
      this.abort = null;
      this.observer?.disconnect();
      this.unsubscribe?.();
      clearTimeout(this.timeout);
    }
    sync(serverVariant) {
      this.picker = this.native.querySelector('variant-selects');
      if (!this.picker) return this.fallback();
      let selected = serverVariant;
      if (!selected) {
        try { selected = JSON.parse(this.picker.querySelector('[data-selected-variant]').textContent); } catch { return this.fallback(); }
      }
      const found = this.variants.find(variant => String(variant.id) === String(selected?.id));
      if (found && typeof selected.available === 'boolean') found.available = selected.available;
      this.current = found;
      this.setBusy(false);
      if (!found?.supported || !nativeSelections(this.picker, found)) return this.fallback();
      if (!this.preferences[found.stamp] || this.nativeSelectionChanged || String(this.preferenceIntent?.id) === String(found.id)) this.preferences[found.stamp] = found.weight;
      this.nativeSelectionChanged = false;
      this.preferenceIntent = null;
      if (this.preferences['ACL PRO']) this.productInfo.dataset.logicProWeight = this.preferences['ACL PRO'];
      this.ui.hidden = false;
      this.dataset.ready = 'true';
      this.picker.querySelectorAll('.product-form__input').forEach((group, index) => { group.hidden = this.managed.has(index) && !this.nativeVisible; });
      this.render();
      if (this.focusChoice) {
        const { dimension, value } = this.focusChoice;
        const button = [...this.ui.querySelectorAll('[data-stamp-choice]')].find(item => item.dataset.dimension === dimension && item.dataset.value === value);
        button?.focus({ preventScroll: true });
        this.focusChoice = null;
      }
    }
    fallback() {
      this.setBusy(false);
      if (this.ui) this.ui.hidden = true;
      delete this.dataset.ready;
      this.native?.querySelectorAll('.product-form__input').forEach(group => { group.hidden = false; });
    }
    showNative() {
      this.nativeVisible = !this.nativeVisible;
      this.querySelector('[data-stamp-show-native]').setAttribute('aria-expanded', String(this.nativeVisible));
      this.picker.querySelectorAll('.product-form__input').forEach((group, index) => { group.hidden = this.managed.has(index) && !this.nativeVisible; });
    }
    setBusy(busy) {
      this.busy = busy;
      if (busy) this.setAttribute('aria-busy', 'true');
      else this.removeAttribute('aria-busy');
      this.ui?.querySelectorAll('[data-stamp-choice]').forEach(button => { button.disabled = busy || button.dataset.available !== 'true'; });
      clearTimeout(this.timeout);
      if (busy) {
        this.status.textContent = this.data.labels.pending;
        this.timeout = setTimeout(() => { this.productInfo.abortController?.abort(); this.fail(); }, 25000);
      } else if (this.status) this.status.textContent = '';
    }
    fail() {
      if (!this.busy) return;
      const selections = nativeSelections(this.native.querySelector('variant-selects'), this.current || {});
      if (selections) applySelections(selections);
      this.setBusy(false);
      this.status.textContent = this.data.labels.error;
      this.productInfo.productForm?.toggleSubmitButton(!this.current?.available, this.current?.available ? undefined : globalThis.variantStrings?.soldOut);
    }
    render() {
      const variantNote = this.ui.querySelector('[data-stamp-variant-note]');
      if (variantNote) variantNote.hidden = this.current.weight !== 'S';
      const scopes = { stamp: {}, series: { stamp: this.current.stamp }, weight: { stamp: this.current.stamp, series: this.current.series } };
      const orders = { stamp: stampOrder, series: seriesOrder, weight: variantOrder };
      for (const dimension of ['stamp', 'series', 'weight']) {
        const list = this.ui.querySelector(`[data-stamp-options="${dimension}"]`);
        const rows = candidates(this.variants, this.current, this.managed, scopes[dimension]);
        const values = orders[dimension].filter(value => rows.some(variant => variant[dimension] === value));
        list.replaceChildren(...values.map(value => {
          const button = document.createElement('button');
          const available = rows.some(variant => variant[dimension] === value && variant.available);
          button.type = 'button';
          button.className = `lsp-choice lsp-choice--${dimension}`;
          button.dataset.stampChoice = '';
          button.dataset.dimension = dimension;
          button.dataset.value = value;
          button.dataset.available = String(available);
          button.setAttribute('aria-pressed', String(value === this.current[dimension]));
          button.disabled = this.busy || !available;
          const label = dimension === 'stamp' && value === 'NONE' ? this.data.labels.unstamped : dimension === 'series' ? value === 'delta-v' ? 'Delta-V' : value[0].toUpperCase() + value.slice(1) : value;
          if (dimension === 'stamp' && this.data.images[value]) {
            const image = document.createElement('img');
            image.src = this.data.images[value];
            image.width = 140;
            image.height = 68;
            image.alt = '';
            image.addEventListener('error', () => { image.hidden = true; }, { once: true });
            button.append(image);
          }
          const text = document.createElement('span');
          text.textContent = label;
          button.append(text);
          if (!available) {
            const soldOut = document.createElement('small');
            soldOut.textContent = this.data.labels.soldOut;
            button.append(soldOut);
          }
          return button;
        }));
      }
    }
    choose(button) {
      if (this.busy || button.disabled || button.getAttribute('aria-pressed') === 'true') return;
      const { dimension, value } = button.dataset;
      const filter = dimension === 'stamp' ? { stamp: value } : dimension === 'series' ? { stamp: this.current.stamp, series: value } : { stamp: this.current.stamp, series: this.current.series, weight: value };
      const chosen = target(this.variants, this.current, this.managed, filter, this.preferences[filter.stamp] || this.current.weight);
      if (chosen) this.selectVariant(chosen, { focus: { dimension, value } });
    }
    selectVariant(variant, options = {}) {
      if ((this.busy && !options.history) || !this.variants.some(item => String(item.id) === String(variant.id))) return false;
      const selections = nativeSelections(this.native.querySelector('variant-selects'), variant);
      if (!selections) { this.fallback(); return false; }
      const changed = selections.find(({ value }, index) => value !== this.current?.options[index]) || selections[0];
      applySelections(selections);
      this.focusChoice = options.focus || null;
      this.preferenceIntent = options.history || options.focus?.dimension === 'weight' ? variant : null;
      this.nativeSelectionChanged = false;
      this.setBusy(true);
      this.internalChange = true;
      (changed.select || changed.control).dispatchEvent(new Event('change', { bubbles: true }));
      this.internalChange = false;
      return true;
    }
  }
  if (typeof customElements !== 'undefined' && !customElements.get('logic-stamp-picker')) customElements.define('logic-stamp-picker', LogicStampPicker);
  if (typeof module !== 'undefined' && module.exports) module.exports = { ...model, LogicStampPicker };
})();

